import { useState, useCallback, useEffect, useMemo } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import find from 'lodash/find';
import head from 'lodash/head';
import { useMutation, useApolloClient, type ApolloCache } from '@apollo/client';
// Skillmore UI Components
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
// local imports
import { ACTIVE_EMPLOYEE_DEV_PLAN } from '../graphql/ActiveEmployeeDevPlan';
import { SEND_MENTORSHIP_REQUEST } from '../graphql/SendMentorshipRequest';
import { COMPLETE_EMPLOYEE_COURSE } from '../graphql/CompleteEmployeeCourse';
import { SELECT_ACTIVITY } from '../graphql/SelectActivity';
import { UPDATE_ACTIVITY } from '../graphql/UpdateActivity';
import { UPDATE_EMPLOYEE_ADVISOR } from '../graphql/UpdateEmployeeAdvisor';
import { EMPLOYEE_DEV_PLAN_QUERY } from '../graphql/EmployeeDevPlan';
import {
  SkillActivity, EmployeeActivityStatus, EmployeeAdvisorStatus,
  ActiveEmployeeDevPlanDocument, SendMentorshipRequestDocument,
  CompleteEmployeeCourseDocument, SelectActivityDocument, UpdateActivityDocument, UpdateEmployeeAdvisorDocument,
  EmployeeDevPlanQuery
} from '../graphql/types';
import {
  AnyDevPlanAdvisor, DevPlanAdvisor, DevPlanCompletedAdvisor, DevPlanCourse,
  EmployeeDevPlanDetails
} from '../graphql/customTypes';
import { evictEmployeeActivities, updateCachedResult } from '../helpers/graphql';

const updateAdvisors = (cache: ApolloCache<unknown>) => {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeProgress' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeAdvisors' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeSkillAdvisors' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'devplanProgresses' });
};

type EmployeeDevPlanActionsParams = {
  devplan_id?: number | null;
  is_active?: boolean | null;
  advisors?: (AnyDevPlanAdvisor | DevPlanCompletedAdvisor)[] | null;
  // for Storybook only
  testAction?: number;
};

const useEmployeeDevPlanActions = ({
  devplan_id,
  is_active,
  advisors,
  testAction
}: EmployeeDevPlanActionsParams) => {
  const { cache: apolloCache } = useApolloClient();

  const { mutate: activateDevPlan, loading: activatePending, failed: activateFailed } = useMutationMethod({
    key: 'activeEmployeeDevPlan',
    mutation: useMutation(ACTIVE_EMPLOYEE_DEV_PLAN as typeof ActiveEmployeeDevPlanDocument)
  });
  const { mutate: sendMentorshipRequest, loading: requestPending, failed: requestFailed } = useMutationMethod({
    key: 'sendMentorshipRequest',
    mutation: useMutation(SEND_MENTORSHIP_REQUEST as typeof SendMentorshipRequestDocument)
  });
  const { mutate: completeCourse, loading: completeCoursePending, failed: completeCourseFailed } = useMutationMethod({
    key: 'completeEmployeeCourse',
    mutation: useMutation(COMPLETE_EMPLOYEE_COURSE as typeof CompleteEmployeeCourseDocument)
  });
  const { mutate: selectActivity, loading: selectPending } = useMutationMethod({
    mutation: useMutation(SELECT_ACTIVITY as typeof SelectActivityDocument)
  });
  const { mutate: updateActivity, loading: updatePending } = useMutationMethod({
    mutation: useMutation(UPDATE_ACTIVITY as typeof UpdateActivityDocument)
  });
  const { mutate: completeActivity, loading: completeActivityPending } = useMutationMethod({
    mutation: useMutation(UPDATE_ACTIVITY as typeof UpdateActivityDocument)
  });
  const { mutate: completeAdvisor, loading: completeAdvisorPending, failed: completeAdvisorFailed } = useMutationMethod({
    mutation: useMutation(UPDATE_EMPLOYEE_ADVISOR as typeof UpdateEmployeeAdvisorDocument)
  });

  const [courseCompletingId, setCourseCompletingId] = useState<number>();
  const [activitySelectingId, setActivitySelectingId] = useState<number>();
  const [activityCompletingId, setActivityCompletingId] = useState<number>();
  const [advisorCompletingId, setAdvisorCompletingId] = useState<number>();
  const [advisorRequestingId, setAdvisorRequestingId] = useState<number>();
  // one failure state for all three activity writes, cleared before each so the snackbar re-opens every time
  const [activityFailed, setActivityFailed] = useState(false);

  const handleActivation = useCallback((value: boolean) => {
    if (devplan_id && Boolean(is_active) !== Boolean(value)) activateDevPlan?.({
      variables: { devplan_id, input: { is_active: Boolean(value) } },
      update: (cache, result) => {
        if (result.data?.activeEmployeeDevPlan?.optimistic) {
          cache.updateQuery({
            query: EMPLOYEE_DEV_PLAN_QUERY, variables: { devplan_id }
          }, (data: EmployeeDevPlanQuery | null) => updateCachedResult(data, 'employeeDevplan', (plan) => ({
            ...(plan || {}) as EmployeeDevPlanDetails, is_active: Boolean(value)
          } as EmployeeDevPlanDetails)));
        } else {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeDevplan' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeDevplans' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeProgress' }); // just to be on the safe side
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'devplanProgresses' });
        }
      },
      optimisticResponse: { activeEmployeeDevPlan: {
        success: true, optimistic: true, __typename: 'MutationResponse'
      } }
    });
  }, [devplan_id, is_active, activateDevPlan]);

  const handleCompleteCourse = useCallback((course?: DevPlanCourse) => {
    const course_id = course?.id;
    if (devplan_id && course_id) {
      setCourseCompletingId(course_id);
      completeCourse?.({
        variables: { course_id, input: {} },
        onCompleted: () => setCourseCompletingId(undefined),
        onError: () => setCourseCompletingId(undefined),
        // no optimistic response: `completed_at` is only known server-side, so flipping
        // `is_complete` early leaves the card with neither the button nor the completed footer —
        // the card must keep its pending state until the real result arrives
        update: (cache) => {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeProgress' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'devplanProgresses' });
        }
      });
    }
  }, [devplan_id, completeCourse]);

  const handleSelectActivity = useCallback((activity: SkillActivity) => {
    const { id: activity_id, employee_activity_id, is_selected, is_complete } = activity;
    if (is_complete || !activity_id) return;
    setActivitySelectingId(activity_id);
    setActivityFailed(false);
    if (employee_activity_id) updateActivity({
      variables: { activity_id: employee_activity_id, input: {
        status: is_selected ? EmployeeActivityStatus.inactive : EmployeeActivityStatus.active
      } },
      // TODO: optimistic response
      update: evictEmployeeActivities,
      onCompleted: () => setActivitySelectingId(undefined),
      onError: () => {
        setActivitySelectingId(undefined);
        setActivityFailed(true);
      }
    });
    else selectActivity({
      variables: { input: { activity_id } },
      // TODO: optimistic response
      update: evictEmployeeActivities,
      onCompleted: () => setActivitySelectingId(undefined),
      onError: () => {
        setActivitySelectingId(undefined);
        setActivityFailed(true);
      }
    });
  }, [selectActivity, updateActivity]);

  const handleCompleteActivity = useCallback((activity: SkillActivity) => {
    const { id: activity_id, is_selected, employee_activity_id } = activity;
    if (!activity_id || !is_selected || !employee_activity_id) return;
    setActivityCompletingId(activity_id);
    setActivityFailed(false);
    completeActivity?.({
      variables: { activity_id: employee_activity_id, input: { status: EmployeeActivityStatus.completed } },
      // TODO: optimistic response
      update: evictEmployeeActivities,
      onCompleted: () => setActivityCompletingId(undefined),
      onError: () => {
        setActivityCompletingId(undefined);
        setActivityFailed(true);
      }
    });
  }, [completeActivity]);

  const handleRequestAdvisor = useMemo(() => advisors ? (id: number) => {
    if (id && sendMentorshipRequest) {
      const skillIds = map(find(advisors, ['id', id])?.advisory_skills, 'id');
      if (size(skillIds) >= 1 && head(skillIds)) {
        setAdvisorRequestingId(id);

        // recursive function to send requests for all advisor's skills
        const sendRequests = () => {
          const skill_id = skillIds.shift();
          const isLast = size(skillIds) < 1;
          if (skill_id) {
            sendMentorshipRequest({
              variables: { input: { skill_id, mentor_id: id } },
              update: isLast ? updateAdvisors : undefined,
              onError: () => setAdvisorRequestingId(undefined),
              onCompleted: () => {
                if (isLast) setAdvisorRequestingId(undefined);
                else sendRequests();
              }
            });
          } else if (isLast) {
            updateAdvisors(apolloCache);
            setAdvisorRequestingId(undefined);
          } else sendRequests();
        };

        sendRequests();
      }
    }
  } : undefined, [advisors, sendMentorshipRequest, apolloCache]);

  const handleCompleteAdvisor = useCallback((advisor?: DevPlanAdvisor) => {
    const advisor_id = advisor?.id;
    if (!advisor_id) return;
    setAdvisorCompletingId(advisor_id);
    completeAdvisor({
      variables: { advisor_id, input: { status: EmployeeAdvisorStatus.completed } },
      // TODO: optimistic response
      update: updateAdvisors,
      onCompleted: () => setAdvisorCompletingId(undefined),
      onError: () => setAdvisorCompletingId(undefined)
    });
  }, [completeAdvisor]);

  // for Storybook & Jest-snapshots testing only
  useEffect(() => {
    if (testAction === 1) activateDevPlan?.({ variables: { devplan_id: 1, input: { is_active: true } } });
    else if (testAction === 2 || testAction === 3) {
      handleCompleteCourse({ id: 6204 } as DevPlanCourse);
    } else if (testAction === 4 || testAction === 5) {
      if (testAction === 4) setAdvisorCompletingId(2);
      completeAdvisor?.({ variables: { advisor_id: 2, input: { status: EmployeeAdvisorStatus.completed } } });
    } else if (testAction === 6 || testAction === 7) {
      if (testAction === 6) setAdvisorRequestingId(3);
      sendMentorshipRequest?.({ variables: { input: { skill_id: 1112, mentor_id: 3 } } });
    }
  }, [testAction, activateDevPlan, handleCompleteCourse, completeAdvisor, sendMentorshipRequest]);

  const pendingAction = activatePending || requestPending ||
    completeCoursePending || selectPending || updatePending || completeActivityPending || completeAdvisorPending ||
    Boolean(courseCompletingId || activityCompletingId || advisorCompletingId || advisorRequestingId);

  return {
    pendingAction,
    handleActivation,
    activatePending,
    activateFailed,
    handleCompleteCourse,
    courseCompletingId,
    completeCourseFailed,
    handleSelectActivity,
    activitySelectingId,
    handleCompleteActivity,
    activityCompletingId,
    activityFailed,
    handleCompleteAdvisor,
    advisorCompletingId,
    completeAdvisorFailed,
    handleRequestAdvisor,
    advisorRequestingId,
    requestFailed
  };
};

export default useEmployeeDevPlanActions;
